import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { getTranslation } from "../components/translations";

const LOGO_URL = "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69891104ffce19fb70f11876/283cb7c40_blackbg.png";

export default function Privacy() {
  const t = (key) => getTranslation(key);
  const navigate = useNavigate();

  usePageMeta({
    title: 'Privacy Policy | Active Gaming',
    description: 'Read the Active Gaming privacy policy. Learn how we collect, use, and protect your personal data when you use VSteps and V4LLUED Guns products and services.',
    ogImage: `${window.location.origin}/v4logo.png`,
    ogUrl: window.location.origin + '/Privacy',
  });

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gray-800/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-gray-700/8 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 bg-[#0B0C10]/80 backdrop-blur-xl border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VSteps" className="h-8 sm:h-10 w-auto" />
          </div>
          
          <Button 
            onClick={() => navigate('/steps')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 bg-transparent"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToHome')}
          </Button>
        </nav>
      </header>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#FF0000]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">{t('privacyPolicy')}</h1>
              <p className="text-[#C5C6C7] mt-2">{t('lastUpdatedPrivacy')}</p>
            </div>
          </div>

          <div className="bg-[#1a1a1a]/60 rounded-3xl p-6 sm:p-8 md:p-12 border border-white/5 space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('introduction')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('privacyIntro')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('infoWeCollect')}</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-white">{t('deviceInfo')}</h3>
                  <p className="text-[#C5C6C7] leading-relaxed">
                    {t('deviceInfoDesc')}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-white">{t('usageData')}</h3>
                  <p className="text-[#C5C6C7] leading-relaxed">
                    {t('usageDataDesc')}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-white">{t('networkInfo')}</h3>
                  <p className="text-[#C5C6C7] leading-relaxed">
                    {t('networkInfoDesc')}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('howWeUse')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed mb-3">{t('howWeUseIntro')}</p>
              <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                <li>{t('useItem1')}</li>
                <li>{t('useItem2')}</li>
                <li>{t('useItem3')}</li>
                <li>{t('useItem4')}</li>
                <li>{t('useItem5')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('dataStorage')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('dataStorageDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('thirdParty')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('thirdPartyDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('yourRights')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed mb-3">{t('yourRightsIntro')}</p>
              <ul className="list-disc list-inside text-[#C5C6C7] space-y-2 ml-4">
                <li>{t('rightItem1')}</li>
                <li>{t('rightItem2')}</li>
                <li>{t('rightItem3')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('changesToPolicy')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('changesToPolicyDesc')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-[#FF0000]">{t('contactUs')}</h2>
              <p className="text-[#C5C6C7] leading-relaxed">
                {t('contactUsDesc')}
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}